import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Alert, 
  Divider,
  CircularProgress,
  Paper
} from '@mui/material';
import { tenantTokenService } from '../../../services/skillcoinService';
import TokenSettingsModal from './TokenSettingsModal';
import TokenExchangeSettings from './TokenExchangeSettings';

const TokenSettings = ({ token: initialToken, onUpdate }) => {
  const [token, setToken] = useState(initialToken || null);
  const [settings, setSettings] = useState({
    allowExternalExchange: false,
    allowPeerTransfer: true,
    minTransactionAmount: 0.01,
    platformFeePercent: 5.0,
    tenantFeePercent: 2.0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  // Initialize with the token prop when it changes
  useEffect(() => {
    if (initialToken) {
      setToken(initialToken);
      
      // Initialize settings from token.settings if available
      if (initialToken.settings) {
        setSettings(initialToken.settings);
      }
      // If settings not available in token, keep default settings
    }
  }, [initialToken]);

  // Extract token fetching logic to a reusable function
  const fetchTokenData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching token data...');
      
      // Add timeout to prevent infinite loading state
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out')), 15000)
      );
      
      // Race the actual request against timeout
      const tokenData = await Promise.race([
        tenantTokenService.getTenantToken(),
        timeoutPromise
      ]);
      
      console.log('Token data received:', tokenData);
      
      // Check if we got a valid response
      if (!tokenData) {
        throw new Error('Received empty response from server');
      }
      
      // Handle different response formats
      if (tokenData.data) {
        // Response with .data property
        const data = tokenData.data.token || tokenData.data;
        setToken(data);
        if (data && data.settings) {
          setSettings(data.settings);
        }
      } else if (tokenData.token) {
        // Response with .token property
        setToken(tokenData.token);
        if (tokenData.token.settings) {
          setSettings(tokenData.token.settings);
        }
      } else {
        // Direct token object
        setToken(tokenData);
        if (tokenData.settings) {
          setSettings(tokenData.settings);
        }
      }
    } catch (err) {
      console.error('Error fetching token data:', err);
      setError('Failed to load token settings. Please try again later.');
      // Still set token to null to show create interface if appropriate
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Open token settings modal
  const handleOpenSettingsModal = () => {
    setOpenSettingsModal(true);
  };

  // Handle token settings update from modal
  const handleTokenUpdate = async (updatedToken) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Updating token with:', updatedToken);
      
      // Check if we're creating or updating
      if (!token) {
        try {
          // First check if there's already a token for this tenant
          const existingToken = await tenantTokenService.getTenantToken();
          
          if (existingToken && (existingToken.data || existingToken.token)) {
            // Token exists - inform user and use update instead
            console.log('Found existing token - updating instead of creating:', existingToken);
            
            // Extract the token from response based on its structure
            const tokenData = existingToken.data || existingToken.token || existingToken;
            
            // Update with new values while keeping ID
            const updateData = {
              ...updatedToken,
              _id: tokenData._id
            };
            
            const updateResult = await tenantTokenService.updateToken(updateData);
            console.log('Update result:', updateResult);
            
            // Refresh token data
            await refreshTokenData();
            setSuccess('Token updated successfully!');
            return;
          }
        } catch (existingTokenError) {
          console.log('Error checking for existing token:', existingTokenError);
          // Continue with token creation if check fails
        }
        
        // Create new token with proper defaults
        const tokenData = {
          ...updatedToken,
          // Initialize empty arrays for required fields
          earningActivities: [],
          usageOptions: [],
          // Set proper default values
          totalSupply: 0,
          maxSupply: 1000000,
          isActive: true
        };
        
        try {
          const createResult = await tenantTokenService.createToken(tokenData);
          console.log('Create result:', createResult);
          await refreshTokenData();
          setSuccess('Token created successfully!');
        } catch (createError) {
          // If creation fails with "token already exists" error, try to get and update the existing token
          if (createError.response?.data?.message?.includes('already exists')) {
            console.log('Token already exists - trying to fetch and update');
            
            // Fetch the existing token
            const existingToken = await tenantTokenService.getTenantToken();
            const tokenData = existingToken.data || existingToken.token || existingToken;
            
            // Update with new values
            const updateData = {
              ...updatedToken,
              _id: tokenData._id
            };
            
            const updateResult = await tenantTokenService.updateToken(updateData);
            console.log('Update result:', updateResult);
            
            // Refresh token data
            await refreshTokenData();
            setSuccess('Updated existing token successfully!');
          } else {
            // Rethrow if it's not the "already exists" error
            throw createError;
          }
        }
      } else {
        // Update existing token
        const updateResult = await tenantTokenService.updateToken(updatedToken);
        console.log('Update result:', updateResult);
        await refreshTokenData();
        setSuccess('Token updated successfully!');
      }
      
      // Notify parent component about the update if callback exists
      if (onUpdate && typeof onUpdate === 'function') {
        await onUpdate();
      }
    } catch (err) {
      console.error('Error updating token:', err);
      // Get the most specific error message available
      const errorMessage = err.response?.data?.message || err.message || `Failed to ${!token ? 'create' : 'update'} token. Please try again.`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh token data
  const refreshTokenData = async () => {
    try {
      const tokenData = await tenantTokenService.getTenantToken();
      console.log('Fresh token data:', tokenData);
      
      // Standardize response format (handle different response structures)
      let newToken = null;
      if (tokenData.data) {
        // Response format may vary between data.token and just data
        newToken = tokenData.data.token || tokenData.data;
      } else if (tokenData.token) {
        newToken = tokenData.token;
      } else {
        // Direct token object
        newToken = tokenData;
      }
      
      // Set token data
      setToken(newToken);
      
      // Update settings if available
      if (newToken) {
        // Ensure we maintain both settings and exchangeSettings
        setSettings(newToken.settings || {});
      }
      
      return newToken;
    } catch (error) {
      console.error('Error refreshing token data:', error);
      setError('Failed to refresh token data. Please try again.');
      return null;
    }
  };

  // If there's an error but token data exists (e.g. settings update failed),
  // render both token settings and the error
  if (token) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              {token.name} ({token.symbol}) Settings
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleOpenSettingsModal}
            >
              Edit Token
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {token.description}
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={fetchTokenData}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <Divider sx={{ mb: 3 }} />
          
          <TokenExchangeSettings 
            token={token} 
            onUpdate={onUpdate} 
          />
        </CardContent>
        
        <TokenSettingsModal
          open={openSettingsModal}
          onClose={() => setOpenSettingsModal(false)}
          token={token}
          onSave={handleTokenUpdate}
          isCreating={false}
        />
      </Card>
    );
  }

  // If we're loading, show a loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  // If we have an error and no token, show error with retry button
  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchTokenData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        <Typography variant="h6" gutterBottom>
          No Token Configured
        </Typography>
        <Typography variant="body2" paragraph>
          You haven't set up a token for your community yet, or we couldn't load your existing token. 
          Create one to start building your token economy.
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleOpenSettingsModal}
        >
          Create Token
        </Button>
        
        <TokenSettingsModal
          open={openSettingsModal}
          onClose={() => setOpenSettingsModal(false)}
          token={null}
          onSave={handleTokenUpdate}
          isCreating={true}
        />
      </Paper>
    );
  }

  // If we have no token and no error, show the create token UI
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        No Token Configured
      </Typography>
      <Typography variant="body2" paragraph>
        You haven't set up a token for your community yet. Create one to start building your token economy.
      </Typography>
      <Button 
        variant="contained" 
        color="primary"
        onClick={handleOpenSettingsModal}
      >
        Create Token
      </Button>
      
      <TokenSettingsModal
        open={openSettingsModal}
        onClose={() => setOpenSettingsModal(false)}
        token={null}
        onSave={handleTokenUpdate}
        isCreating={true}
      />
    </Paper>
  );
};

export default TokenSettings;